import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Wifi, Building2, LogOut, ShieldCheck, Megaphone, X, Menu } from "lucide-react";
import { navFor, PROGRAMA } from "../config";
import { isEnabled } from "../lib/flags";
import { useAllowedRooms, useParticipantNombre } from "../lib/access";
import { salaPermitida } from "../lib/salas";
import { fetchMessages, dismissed, dismiss, type Message } from "../lib/messages";
import { alExpirarSesion, sesionEstaExpirada, volverAlLogin } from "../lib/auth";
import { alcanceLabel } from "../lib/sites";
import { getYo, nombreCompleto, etiquetaDeCuenta, type Identidad } from "../lib/identidad";
import { useSitesEstado } from "./SiteFilter";
import type { Session } from "../types";
import { Icon } from "./Icon";
import { AppMark } from "./AppMark";
import { FirmaGobernadora } from "./FirmaGobernadora";
import { Version } from "./Version";

export function Layout({
  session,
  onLogout,
  children,
}: {
  session: Session;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>(() => [...dismissed()]);
  const [avisos, setAvisos] = useState<Message[]>([]);
  const { sites, cargando: cargandoSedes } = useSitesEstado();

  /*
   * QUIEN ESTA DENTRO. Solo para personal.
   *
   * El participante conserva su camino: su nombre sale de `/me`, que lo devuelve
   * en null cuando el registro fue POR APARTAMENTO — eso es la proteccion de
   * privacidad de un menor, no un hueco, y pedirlo por otra via la saltaria.
   *
   * NO BLOQUEA NADA. Si `/yo` falla se queda en null y la caja enseña el
   * respaldo. Cambiar un rotulo que no carga por un panel que no abre seria un
   * mal negocio.
   */
  const [identidad, setIdentidad] = useState<Identidad | null>(null);
  useEffect(() => {
    if (session.role !== "librarian") return;
    let vivo = true;
    void getYo().then((i) => { if (vivo) setIdentidad(i); });
    return () => { vivo = false; };
  }, [session.role, session.user]);

  /*
   * El rótulo del sidebar es el ALCANCE de quien mira, no un residencial fijo.
   * Antes decía "Residencial Quintana" a todo el mundo —incluido quien administra
   * los 122— porque salía de una constante de config.ts.
   */
  const alcance = alcanceLabel(session.cognitoRole, sites, PROGRAMA.program, cargandoSedes);

  // El estudiante ve las salas encendidas globalmente (web) Y habilitadas para su cuenta;
  // la bibliotecaria ve su panel siempre.
  // El override de salas por persona vivía en localStorage: en PCs compartidas de
  // biblioteca estaba roto por diseño (el niño cambiaba de máquina y perdía su
  // configuración; el siguiente heredaba la del anterior). Se eliminó con las
  // cuentas demo; lo sustituirá `assignments`, que vive en la BD.
  // Salas permitidas del participante (contexto de AccessGate; null = todas). El nav
  // respeta tanto el flag global (isEnabled) como el acceso por sala del usuario.
  const allowedRooms = useAllowedRooms();
  /*
   * El nombre, no el identificador interno. Aqui salia `u-2cb79b4d`, que no le
   * dice nada a nadie: ni al nino que lo lee ni a la bibliotecaria que mira por
   * encima del hombro para saber quien dejo la sesion abierta.
   *
   * El STAFF ya no se cae al usuario: su nombre y su correo salen de `/yo`
   * (arriba). Quien se sigue cayendo es el menor registrado POR APARTAMENTO,
   * que no tiene nombre a proposito.
   */
  const nombreParticipante = useParticipantNombre();

  // Que dice la caja del pie: nombre si se sabe, correo si no, y el usuario como
  // ultimo recurso. El orden vive en `identidad.ts`, con sus pruebas.
  const cuenta = etiquetaDeCuenta(
    nombreParticipante ?? nombreCompleto(identidad),
    identidad?.email ?? null,
    session.user
  );
  /*
   * El caso especial de "refuerzo para adultos no" desaparecio con la m038:
   * ahora es una sala controlable y basta con no ponerla en la plantilla adulta.
   * Una regla menos escondida en el codigo y una casilla mas en el panel.
   */
  const nav = navFor(session.role, session.cognitoRole).filter(
    (item) => session.role === "librarian"
      || (isEnabled(item.key) && salaPermitida(item.key, allowedRooms))
  );

  // Los avisos son de la sede y salen del servidor, que ya filtra por el token:
  // el estudiante recibe solo los suyos y los de su grupo.
  useEffect(() => {
    if (session.role !== "student") return;
    let alive = true;
    fetchMessages({ soloActivos: true }).then((m) => alive && setAvisos(m));
    return () => { alive = false; };
  }, [session.role, session.user]);

  const mensajes = avisos.filter((m) => !hidden.includes(m.id));

  // Sesión caída: se avisa arriba y se deja al usuario decidir. Antes esto era una
  // expulsión automática y echaba a la gente a mitad de trabajo.
  const [caida, setCaida] = useState(sesionEstaExpirada);
  useEffect(() => alExpirarSesion(() => setCaida(true)), []);

  function cerrarMsg(id: string) { dismiss(id); setHidden((h) => [...h, id]); }

  return (
    <div className="app">
      {/* Primer elemento enfocable (WCAG 2.4.1): sin él, quien navega con
          teclado atraviesa el menú entero en cada pantalla. No es un enlace
          normal a "#contenido" porque con HashRouter eso cambiaría la RUTA y
          acabaría en el comodín: se mueve el foco a mano. */}
      <a className="saltar-contenido" href="#contenido" onClick={saltarAlContenido}>Saltar al contenido</a>
      {menuOpen && <div className="drawer-overlay" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
        <button className="drawer-close" onClick={() => setMenuOpen(false)} aria-label="Cerrar menú"><X size={20} /></button>
        <div className="brand">
          <AppMark size={44} />
          <div className="name">
            {alcance.kicker && <small>{alcance.kicker}</small>}
            <strong>{alcance.titulo}</strong>
          </div>
        </div>

        {/*
          La ficha institucional: de quién es este servicio. El logo de AVP ya
          lleva "Gobierno de Puerto Rico" bajo el escudo, así que la firma no
          cambia de tema — continúa la misma frase.

          Va AQUÍ y no al pie de la columna, debajo de "Modo seguro", que era
          donde estaba primero. Al pie no estorbaba a nadie, pero tampoco la
          veía nadie. Medido en el navegador, en un viewport de 978 px:

            - sin firma, el sidebar ya tenía ~1.048 px de contenido (desbordaba 70);
            - con la firma al pie, 1.172 px, y la firma caía 194 px bajo el corte;
            - con la firma en la ficha, 1.157 px, y se ve sin hacer scroll.

          O sea: el desbordamiento existía ANTES de tocar nada, así que "no
          empujar la navegación" no compraba nada. Lo único que cambia al subirla
          es el orden, y con él si alguien la ve.
        */}
        <div className="agency-card">
          <img src="/assets/avp-logo.png" alt={PROGRAMA.agency} onError={hideBrokenImg} />
          <FirmaGobernadora ancho={110} cargo="Gobernadora" />
        </div>

        <nav className="nav">
          {nav.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <Icon name={item.icon} size={20} />
              <span>{item.label}</span>
              {item.soon && <span className="soon-badge">soon</span>}
            </NavLink>
          ))}
        </nav>

        {/*
          Quien esta dentro. Enseñaba `u-93cf92ee` —el username que genera Cognito
          con el patron `u-${id.slice(0,8)}`— que a una persona no le dice nada. Y
          el sentido de esta caja es saber en que cuenta estas ANTES de hacer algo:
          en un equipo compartido, esa duda acaba en "lo hice desde la cuenta de
          otra".

          El escudo se queda: es el que sigue diciendo "modo seguro" cuando el
          rotulo pasa a decir un nombre.
        */}
        <div className="safe-mode">
          <ShieldCheck size={22} />
          <div>
            <strong>{cuenta.titulo}</strong>
            <small>{cuenta.detalle}</small>
          </div>
        </div>
        <Version />
      </aside>

      <main className="main" id="contenido" tabIndex={-1}>
        <div className="mobile-topbar">
          <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Abrir menú"><Menu size={22} /></button>
          <div className="mt-brand">
            <AppMark size={34} />
            <strong>{alcance.titulo}</strong>
          </div>
          {/*
            NO en el kiosco: ver el comentario del botón gemelo en `.topbar`
            más abajo — es el mismo botón, en la barra móvil.
          */}
          {session.role !== "kiosk" && (
            <button className="mt-exit" onClick={onLogout} aria-label="Salir"><LogOut size={20} /></button>
          )}
        </div>

        <div className="topbar">
          <div className="topbar-actions">
            <div className="pill">
              <Wifi size={16} style={{ color: "#1c885b" }} /> Contenido actualizado hoy, {new Date().toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })}
            </div>
            <div className="pill" title={alcance.detalle}>
              <Building2 size={16} /> {alcance.detalle}
            </div>
            {/*
             * OCULTO para el kiosco. La sesión de un kiosco la abre la
             * bibliotecaria por la mañana y la pantalla queda a la vista del
             * público: un clic de un visitante cerraría la sesión y dejaría la
             * sede en la pantalla de login hasta que la bibliotecaria vuelva
             * con una contraseña de hasta 86 caracteres — justo lo que el
             * temporizador de inactividad (`KioscoIdle`) existe para evitar,
             * sin lograrlo si el botón sigue ahí para pulsarlo a propósito o
             * por accidente.
             *
             * CÓMO SE CIERRA ENTONCES, y conviene leerlo entero porque no es
             * equivalente: **cerrar la pestaña**. Eso mata el `sessionStorage`,
             * donde viven la sesión y el id token.
             *
             * RECARGAR NO SIRVE: `sessionStorage` sobrevive a una recarga en la
             * misma pestaña — es justo de lo que depende `KioscoIdle` para
             * resetear la pantalla sin cerrar la sesión. Decir lo contrario aquí
             * mandaría a la bibliotecaria a dejar el centro con la sesión viva.
             *
             * Los tokens de Cognito —el refresh incluido— también viven en el
             * `sessionStorage` de la pestaña (lib/cognito.ts), así que cerrarla
             * los borra de la máquina. Antes vivían en `localStorage` y se
             * quedaban en la computadora pública hasta caducar. Lo que además
             * los revoca en el servidor es regenerar la contraseña desde el
             * panel, que cierra las sesiones abiertas (`AdminUserGlobalSignOut`).
             *
             * Si algún día se quiere una salida deliberada sin botón visible
             * —un atajo, una ruta `#/salir`—, este es el sitio.
             */}
            {session.role !== "kiosk" && (
              <button className="pill dark" onClick={onLogout}>
                <LogOut size={16} /> Salir
              </button>
            )}
          </div>
        </div>

        {/*
          En el kiosco quien ve esto es el público, no quien tiene la contraseña:
          «Volver a entrar» lo llevaría a la pantalla de login de la cuenta del
          centro. Se le pide que avise al personal, que es quien sabe entrar, y no
          se le da botón — por la misma razón que el kiosco no tiene «Salir».
        */}
        {caida && session.role === "kiosk" && (
          <div className="announce sesion-caida">
            <ShieldCheck size={20} className="announce-ic" />
            <div className="announce-body">
              <strong>Sesión del kiosco vencida</strong>
              <span>Avise al personal de la biblioteca para que la vuelva a abrir.</span>
            </div>
          </div>
        )}
        {caida && session.role !== "kiosk" && (
          <div className="announce sesion-caida">
            <ShieldCheck size={20} className="announce-ic" />
            <div className="announce-body">
              <strong>Tu sesión ya no es válida</strong>
              <span>
                El servidor rechazó tus credenciales y no se pudieron renovar. Lo que ya
                guardaste está a salvo. Vuelve a entrar para seguir.
              </span>
            </div>
            <button className="btn dark" onClick={volverAlLogin}>Volver a entrar</button>
          </div>
        )}

        {mensajes.map((m) => (
          <div className="announce" key={m.id}>
            <Megaphone size={20} className="announce-ic" />
            <div className="announce-body">
              <strong>{m.title}</strong>
              <span>{m.body}</span>
            </div>
            <button className="announce-x" onClick={() => cerrarMsg(m.id)} aria-label="Cerrar"><X size={16} /></button>
          </div>
        ))}

        {children}
      </main>
    </div>
  );
}

function saltarAlContenido(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  document.getElementById("contenido")?.focus();
}

function hideBrokenImg(e: React.SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.style.display = "none";
}
